import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { submitDonorContact, submitPartnerInquiry, submitSupportRequest } from '@/lib/api/public'

const PINK = '#d6336c'
const DEEP = '#a61b5e'
const GOLD = '#f0a23a'

const Input = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input {...props} className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500" />
)
const Textarea = (props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea {...props} className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none" />
)
const Submit = ({ children, disabled }: { children: React.ReactNode; disabled?: boolean }) => (
  <button disabled={disabled} className="inline-flex items-center justify-center rounded-xl px-5 py-3 font-semibold text-white shadow-sm disabled:opacity-50" style={{ background: PINK }}>{children}</button>
)

const DAREPage: React.FC = () => {
  const [help, setHelp] = useState({ first_name:'', last_name:'', phone:'', email:'', county:'', household_size:'', needs:[] as string[], notes:'' })
  const [donor, setDonor] = useState({ name:'', email:'', phone:'', donor_type:'individual', notes:'' })
  const [partner, setPartner] = useState({ organization_name:'', contact_name:'', email:'', phone:'', county:'', notes:'' })
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState<'help' | 'donor' | 'partner' | null>(null)

  const [weather, setWeather] = useState({ temperature: null as number | null, wind: null as number | null, precipitation: null as number | null, code: null as number | null, alerts: [] as Array<{ event: string; headline: string | null }>, checkedAt: null as string | null, error: false })
  useEffect(() => {
    let cancelled = false
    const loadWeather = async () => {
      try {
        const [conditionsResponse, alertsResponse] = await Promise.all([
          fetch('https://api.open-meteo.com/v1/forecast?latitude=30.4383&longitude=-84.2807&current=temperature_2m,wind_speed_10m,precipitation,weather_code&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=America%2FNew_York'),
          fetch('https://api.weather.gov/alerts/active?point=30.4383,-84.2807', { headers: { Accept: 'application/geo+json', 'User-Agent': 'WEE-Guided-Access/1.0 (info@weecarediaperbank.org)' } }),
        ])
        if (!conditionsResponse.ok || !alertsResponse.ok) throw new Error('Weather service unavailable')
        const conditions = await conditionsResponse.json()
        const alerts = await alertsResponse.json()
        if (cancelled) return
        setWeather({ temperature: conditions.current?.temperature_2m ?? null, wind: conditions.current?.wind_speed_10m ?? null, precipitation: conditions.current?.precipitation ?? null, code: conditions.current?.weather_code ?? null, alerts: (alerts.features ?? []).slice(0, 3).map((item: any) => ({ event: item.properties?.event ?? 'Weather alert', headline: item.properties?.headline ?? null })), checkedAt: new Date().toISOString(), error: false })
      } catch {
        if (!cancelled) setWeather(prev => ({ ...prev, error: true, checkedAt: new Date().toISOString() }))
      }
    }
    loadWeather()
    const interval = window.setInterval(loadWeather, 10 * 60 * 1000)
    return () => { cancelled = true; window.clearInterval(interval) }
  }, [])
  const weatherLabel = (code: number | null) => {
    if (code === null) return 'Current conditions unavailable'
    if ([95, 96, 99].includes(code)) return 'Thunderstorms'
    if ([80, 81, 82].includes(code)) return 'Rain showers'
    if ([61, 63, 65, 66, 67].includes(code)) return 'Rain'
    if ([1, 2, 3].includes(code)) return 'Partly cloudy'
    if (code === 0) return 'Clear'
    return 'Current conditions'
  }

  const toggleNeed = (need: string) => setHelp(v => ({ ...v, needs: v.needs.includes(need) ? v.needs.filter(n => n !== need) : [...v.needs, need] }))

  const submitHelp = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    if (!help.first_name || !help.last_name || !help.phone || !help.county || !help.needs.length) {
      setMessage('Please complete your name, phone, county, and at least one emergency need.')
      return
    }
    setSubmitting('help')
    try {
      await submitSupportRequest({
        first_name: help.first_name, last_name: help.last_name, phone: help.phone, email: help.email,
        county: help.county, household_size: help.household_size ? Number(help.household_size) : undefined,
        needs: help.needs, priority: 'immediate_crisis',
        notes: `DA.RE DISASTER RESPONSE REQUEST — ${help.notes || 'No additional details provided.'}`,
        consent_followup: true,
      })
      setMessage('Your DA.RE assistance request has been received. W.E.E. will review it through Guided Access and follow up when appropriate.')
      setHelp({ first_name:'', last_name:'', phone:'', email:'', county:'', household_size:'', needs:[], notes:'' })
    } catch (err) { setMessage(err instanceof Error ? err.message : 'We could not submit the request. Please email W.E.E. for assistance.') }
    finally { setSubmitting(null) }
  }

  const submitDonor = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    if (!donor.name || !donor.email) { setMessage('Please provide your name/organization and email.'); return }
    const parts = donor.name.trim().split(/\s+/)
    setSubmitting('donor')
    try {
      await submitDonorContact({
        first_name: parts[0], last_name: parts.slice(1).join(' '), email: donor.email, phone: donor.phone,
        donor_type: donor.donor_type,
        notes: `DA.RE DISASTER RESPONSE SUPPORT OFFER — ${donor.notes || 'No additional details provided.'}`,
      })
      setMessage('Thank you. Your DA.RE support offer has been received for W.E.E. review.')
      setDonor({ name:'', email:'', phone:'', donor_type:'individual', notes:'' })
    } catch (err) { setMessage(err instanceof Error ? err.message : 'We could not submit your offer. Please contact W.E.E.') }
    finally { setSubmitting(null) }
  }

  const submitPartner = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    if (!partner.organization_name || !partner.contact_name || !partner.email) { setMessage('Please provide the organization, contact name, and email.'); return }
    setSubmitting('partner')
    try {
      await submitPartnerInquiry({
        organization_name: partner.organization_name, contact_name: partner.contact_name, email: partner.email,
        phone: partner.phone, county: partner.county,
        agency_type: 'DA.RE Disaster Response / Recovery Partner',
        notes: partner.notes,
      })
      setMessage('Thank you. Your organization has been submitted for DA.RE partnership coordination.')
      setPartner({ organization_name:'', contact_name:'', email:'', phone:'', county:'', notes:'' })
    } catch (err) { setMessage(err instanceof Error ? err.message : 'We could not submit the partnership offer. Please contact W.E.E.') }
    finally { setSubmitting(null) }
  }

  const needs = [
    ['diaper_distribution','Diapers & infant supplies'], ['formula_assistance','Formula / feeding supplies'],
    ['food_assistance','Food & basic necessities'], ['clothing_assistance','Clothing & hygiene'],
    ['transportation','Transportation'], ['family_stabilization','Recovery & stabilization'],
    ['ess_benefits_navigation','Benefits / resource navigation'], ['utility_assistance','Utilities / household needs'],
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="relative overflow-hidden text-white" style={{ background: `linear-gradient(135deg, ${DEEP}, ${PINK})` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/80">W.E.E. D.A.RE. • Disaster Assistance & Recovery Efforts</p>
          <h1 className="mt-3 text-4xl md:text-6xl font-bold leading-tight">When disaster happens, W.E.E. helps communities stay connected.</h1>
          <p className="mt-5 text-lg md:text-xl text-white/90 max-w-3xl leading-relaxed">A dedicated pathway for preparedness, essential supplies, recovery support, resource navigation, community response, donors, volunteers, and organizations that want to help.</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="#assistance" className="rounded-2xl bg-white px-5 py-3 font-bold" style={{ color: DEEP }}>I Need Assistance →</a>
            <a href="#support-dare" className="rounded-2xl border-2 border-white/60 px-5 py-3 font-bold text-white">I Want to Support D.A.RE. →</a>
          </div>
        </div>
      </section>

      <section className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
          <div className="rounded-2xl border border-amber-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-amber-700">🚨 Live Weather & Emergency Alerts <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-700">LIVE</span></p>
                <h2 className="mt-1 text-lg font-bold text-gray-900">Tallahassee / Leon County conditions</h2>
                <p className="mt-2 text-sm text-gray-600">
                  {weather.temperature !== null ? `${Math.round(weather.temperature)}°F · ${weatherLabel(weather.code)} · Wind ${Math.round(weather.wind ?? 0)} mph` : "Live conditions are loading…"}
                  {weather.precipitation !== null ? ` · Precipitation ${weather.precipitation.toFixed(1)} mm` : ""}
                </p>
              </div>
              <div className="text-xs text-gray-400 text-right">
                <p>{weather.checkedAt ? `Updated ${new Date(weather.checkedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : "Checking live data…"}</p>
                <p>Auto-refreshes every 10 minutes</p>
              </div>
            </div>
            {weather.alerts.length > 0 ? (
              <div className="mt-4 space-y-2">
                {weather.alerts.map((alert, index) => (
                  <div key={index} className="rounded-xl border border-red-200 bg-red-50 p-3">
                    <p className="text-sm font-bold text-red-800">{alert.event}</p>
                    {alert.headline && <p className="text-xs text-red-700 mt-1">{alert.headline}</p>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-4 text-xs text-gray-500">{weather.error ? "Live weather data is temporarily unavailable." : "No active NWS alerts are currently being returned for the Tallahassee point."}</p>
            )}
            <div className="mt-4 flex flex-wrap gap-3 text-sm font-semibold">
              <a href="https://www.nhc.noaa.gov/" target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">National Hurricane Center →</a>
              <a href="https://www.floridadisaster.org/current-weather-updates/" target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">Florida Disaster updates →</a>
              <a href="https://www.floridadisaster.org/info/info2/" target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">Shelters / emergency information →</a>
            </div>
          </div>
        </div>
      </section>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-10">
        {message && <div role="status" aria-live="polite" className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] w-[min(92vw,42rem)] rounded-2xl border-2 border-emerald-300 bg-white p-4 text-sm font-semibold text-gray-900 shadow-2xl"><div className="flex items-start gap-3"><span className="text-2xl" aria-hidden="true">{message.toLowerCase().includes('could not') || message.toLowerCase().includes('please complete') || message.toLowerCase().includes('please provide') || message.toLowerCase().includes('error') ? '⚠️' : '✅'}</span><p className="flex-1">{message}</p><button type="button" onClick={() => setMessage('')} className="rounded-lg px-2 py-1 text-gray-500 hover:bg-gray-100" aria-label="Dismiss message">✕</button></div></div>}

        <section className="grid md:grid-cols-3 gap-4">
          {[
            ['🌸','Families & Individuals','Request emergency essentials, recovery support, transportation, navigation, and stabilization.','#fdf2f7'],
            ['🤝','Organizations','Offer supplies, services, volunteers, referrals, logistics, or recovery capacity.','#fff7ed'],
            ['💛','Donors & Community Supporters','Offer financial or in-kind support so W.E.E. can prepare before needs peak.','#fffbeb'],
          ].map(([icon,title,desc,bg]) => (
            <div key={title} className="rounded-3xl border border-gray-200 p-6" style={{ background:bg }}>
              <div className="text-3xl">{icon}</div><h2 className="mt-3 font-bold text-xl text-gray-900">{title}</h2><p className="mt-2 text-sm text-gray-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </section>

        <section className="rounded-3xl border border-brand-100 bg-white p-7 md:p-9">
          <p className="text-xs font-bold uppercase tracking-wider" style={{color:PINK}}>What D.A.RE. Can Provide</p>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              ['🍼','Material & Infant Essentials','Diapers, wipes, formula, breastfeeding supplies, and other essential infant items when available.'],
              ['🧼','Emergency & Hygiene Supplies','Household hygiene, cleaning and personal-care essentials when available.'],
              ['🍎','Food & Basic Necessities','Support with available food and household resources through W.E.E. or referral partners.'],
              ['🏠','Recovery & Stabilization','Resource navigation and coordinated follow-up after an emergency.'],
              ['🧭','Resource Navigation','Help identifying community resources, referrals, and appropriate recovery pathways.'],
              ['🤲','Community Response','Coordination with organizations, businesses, donors, volunteers, and recovery partners.'],
            ].map(([icon,title,desc]) => <div key={title} className="rounded-2xl border border-gray-100 bg-gray-50 p-5"><span className="text-2xl">{icon}</span><h3 className="font-bold mt-2">{title}</h3><p className="text-sm text-gray-600 mt-1 leading-relaxed">{desc}</p></div>)}
          </div>
        </section>

        <section id="assistance" className="rounded-3xl border border-gray-200 bg-white p-7 md:p-9">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-wider" style={{color:PINK}}>🌸 Need Assistance</p>
            <h2 className="mt-1 text-3xl font-bold text-gray-900">Tell W.E.E. what your household needs.</h2>
            <p className="mt-2 text-sm text-gray-600">This request enters Guided Access as a DA.RE. disaster-response request. It does not guarantee immediate service or emergency response. For immediate danger, call 911 and follow local emergency instructions.</p>
          </div>
          <div className="mt-6 rounded-2xl border-2 border-amber-200 bg-amber-50 p-5 md:p-6" role="status">
            <h3 className="font-bold text-gray-900">Temporary Intake Pause</h3>
            <p className="mt-2 text-sm leading-relaxed text-gray-700">W.E.E. has temporarily reached its current service capacity and is working through existing inquiries. New D.A.RE. assistance requests are paused for now. We will share updates when intake reopens. For immediate danger, call 911 and follow local emergency instructions; for community resource navigation, contact 211.</p>
          </div>
        </section>

        <section id="support-dare" className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-3xl border border-gray-200 bg-white p-7">
            <p className="text-xs font-bold uppercase tracking-wider" style={{color:GOLD}}>💛 Donors & In-Kind Support</p>
            <h2 className="mt-1 text-2xl font-bold">Help W.E.E. prepare before needs peak.</h2>
            <p className="mt-2 text-sm text-gray-600">Financial support is often the most flexible disaster contribution. If you offer supplies, please tell us what you have so W.E.E. can confirm need, storage, timing, and distribution capacity before anything is delivered.</p>
            <form onSubmit={submitDonor} className="mt-5 space-y-3">
              <Input required placeholder="Name or organization" value={donor.name} onChange={e=>setDonor(v=>({...v,name:e.target.value}))} />
              <Input required type="email" placeholder="Email" value={donor.email} onChange={e=>setDonor(v=>({...v,email:e.target.value}))} />
              <Input placeholder="Phone (optional)" value={donor.phone} onChange={e=>setDonor(v=>({...v,phone:e.target.value}))} />
              <select value={donor.donor_type} onChange={e=>setDonor(v=>({...v,donor_type:e.target.value}))} className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm bg-white">
                <option value="individual">Individual / family</option><option value="business">Business</option><option value="foundation">Foundation</option><option value="in_kind">In-kind / supplies</option>
              </select>
              <Textarea rows={4} placeholder="What would you like to contribute? (money, diapers, hygiene supplies, food, transportation, storage, etc.)" value={donor.notes} onChange={e=>setDonor(v=>({...v,notes:e.target.value}))} />
              <Submit disabled={submitting !== null}>{submitting === 'donor' ? 'Submitting…' : 'Offer DA.RE Support →'}</Submit>
            </form>
          </div>

          <div className="rounded-3xl border border-gray-200 bg-white p-7">
            <p className="text-xs font-bold uppercase tracking-wider" style={{color:DEEP}}>🤝 Organizations & Community Partners</p>
            <h2 className="mt-1 text-2xl font-bold">Coordinate capacity with W.E.E.</h2>
            <p className="mt-2 text-sm text-gray-600">Organizations can offer supplies, volunteers, transportation, storage, food, hygiene resources, recovery services, or other response capacity. W.E.E. will review the offer and coordinate when appropriate.</p>
            <form onSubmit={submitPartner} className="mt-5 space-y-3">
              <Input required placeholder="Organization name" value={partner.organization_name} onChange={e=>setPartner(v=>({...v,organization_name:e.target.value}))} />
              <Input required placeholder="Contact name" value={partner.contact_name} onChange={e=>setPartner(v=>({...v,contact_name:e.target.value}))} />
              <Input required type="email" placeholder="Email" value={partner.email} onChange={e=>setPartner(v=>({...v,email:e.target.value}))} />
              <Input placeholder="Phone" value={partner.phone} onChange={e=>setPartner(v=>({...v,phone:e.target.value}))} />
              <Input placeholder="County / service area" value={partner.county} onChange={e=>setPartner(v=>({...v,county:e.target.value}))} />
              <Textarea rows={4} placeholder="What disaster-response capability can your organization offer?" value={partner.notes} onChange={e=>setPartner(v=>({...v,notes:e.target.value}))} />
              <Submit disabled={submitting !== null}>{submitting === 'partner' ? 'Submitting…' : 'Offer Organization Support →'}</Submit>
            </form>
          </div>
        </section>

        <section className="rounded-3xl border border-amber-200 bg-amber-50 p-7">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-700">🧰 Be Ready Before the Next Strike</p>
          <h2 className="mt-1 text-2xl font-bold">Preparedness is part of response.</h2>
          <div className="mt-4 grid md:grid-cols-3 gap-4 text-sm text-gray-700">
            <div><strong>Families:</strong> Keep several days of water, food, medications, infant supplies, chargers, documents, and a communication plan ready.</div>
            <div><strong>Organizations:</strong> Know your capabilities, point of contact, storage capacity, volunteer needs, and what you can activate safely.</div>
            <div><strong>Donors:</strong> Confirm what is actually needed before collecting or delivering goods. W.E.E. will coordinate needs when capacity allows.</div>
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <a href="https://www.ready.gov/hurricanes" target="_blank" rel="noreferrer" className="rounded-xl bg-white border border-amber-200 px-4 py-2.5 text-sm font-semibold text-gray-700">Ready.gov Hurricane Guide →</a>
            <a href="https://www.volunteerflorida.org/serving-during-disasters/" target="_blank" rel="noreferrer" className="rounded-xl bg-white border border-amber-200 px-4 py-2.5 text-sm font-semibold text-gray-700">Volunteer Florida Disaster Guidance →</a>
            <Link to="/" className="rounded-xl px-4 py-2.5 text-sm font-semibold text-white" style={{background:PINK}}>Back to Guided Access →</Link>
          </div>
        </section>
      </main>
    </div>
  )
}

export default DAREPage
